import { useState, useEffect, useCallback } from 'react'
import { getNexusClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth/context'

export interface ActiveMeeting {
  id: string
  roomId: string
  title: string
  description?: string
  creatorId?: string
  creatorName: string
  isPublic: boolean
  participantCount: number
  maxParticipants: number
  createdAt: string
  lastActivityAt: string
  participants?: MeetingParticipant[]
}

export interface MeetingParticipant {
  id: string
  meetingId: string
  participantIdentity: string
  displayName: string
  adminUserId?: string
  isActive: boolean
  joinedAt: string
  leftAt?: string
}

interface UseActiveMeetingsReturn {
  meetings: ActiveMeeting[]
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
  createMeeting: (roomId: string, title: string, isPublic?: boolean) => Promise<ActiveMeeting | null>
  joinMeeting: (roomId: string, displayName: string, participantIdentity: string) => Promise<void>
  leaveMeeting: (roomId: string, participantIdentity: string) => Promise<void>
  deleteMeeting: (roomId: string) => Promise<void>
  cleanupStaleMeetings: () => Promise<void>
  forceDeleteAllMeetings: () => Promise<void>
}

export function useActiveMeetings(): UseActiveMeetingsReturn {
  const [meetings, setMeetings] = useState<ActiveMeeting[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { adminUser } = useAuth()

  // Fetch public meetings
  const fetchMeetings = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const supabase = getNexusClient()

      // Fetch public meetings with participant count > 0
      const { data, error: fetchError } = await supabase
        .from('active_meetings')
        .select(`
          id,
          room_id,
          title,
          description,
          creator_id,
          creator_name,
          is_public,
          participant_count,
          max_participants,
          created_at,
          last_activity_at,
          meeting_participants (
            id,
            participant_identity,
            display_name,
            admin_user_id,
            is_active,
            joined_at
          )
        `)
        .eq('is_public', true)
        .gt('participant_count', 0)
        .order('participant_count', { ascending: false })

      if (fetchError) {
        // Table might not exist yet
        if (fetchError.code === '42P01') {
          console.log('Active meetings table not yet created')
          setMeetings([])
          return
        }
        throw fetchError
      }

      const formattedMeetings: ActiveMeeting[] = (data || [])
        .map((m: any) => {
          // Filter to only active participants
          const activeParticipants = m.meeting_participants?.filter((p: any) => p.is_active) || []

          return {
            id: m.id,
            roomId: m.room_id,
            title: m.title,
            description: m.description,
            creatorId: m.creator_id,
            creatorName: m.creator_name,
            isPublic: m.is_public,
            // Use actual active participant count, not stored value
            participantCount: activeParticipants.length,
            maxParticipants: m.max_participants,
            createdAt: m.created_at,
            lastActivityAt: m.last_activity_at,
            participants: activeParticipants.map((p: any) => ({
              id: p.id,
              meetingId: m.id,
              participantIdentity: p.participant_identity,
              displayName: p.display_name,
              adminUserId: p.admin_user_id,
              isActive: p.is_active,
              joinedAt: p.joined_at,
            })),
          }
        })
        // Filter out meetings with no active participants
        .filter((m) => m.participantCount > 0)

      setMeetings(formattedMeetings)
    } catch (err) {
      console.error('Failed to fetch active meetings:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch meetings')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Create a new meeting
  const createMeeting = useCallback(async (
    roomId: string,
    title: string,
    isPublic: boolean = true
  ): Promise<ActiveMeeting | null> => {
    try {
      const supabase = getNexusClient()

      const creatorName = [adminUser?.firstName, adminUser?.lastName]
        .filter(Boolean)
        .join(' ') || adminUser?.email || 'Anonymous'

      // Cast to any to bypass TypeScript errors (table might not exist in schema yet)
      const { data, error: insertError } = await (supabase
        .from('active_meetings') as any)
        .upsert({
          room_id: roomId,
          title,
          creator_id: adminUser?.id,
          creator_name: creatorName,
          is_public: isPublic,
          participant_count: 0,
        }, {
          onConflict: 'room_id',
        })
        .select()
        .single()

      if (insertError) {
        // Table might not exist
        if (insertError.code === '42P01') {
          console.log('Active meetings table not yet created')
          return null
        }
        throw insertError
      }

      const d = data as any
      return {
        id: d.id,
        roomId: d.room_id,
        title: d.title,
        description: d.description,
        creatorId: d.creator_id,
        creatorName: d.creator_name,
        isPublic: d.is_public,
        participantCount: d.participant_count,
        maxParticipants: d.max_participants,
        createdAt: d.created_at,
        lastActivityAt: d.last_activity_at,
      }
    } catch (err) {
      console.error('Failed to create meeting:', err)
      return null
    }
  }, [adminUser])

  // Join a meeting (add participant)
  const joinMeeting = useCallback(async (
    roomId: string,
    displayName: string,
    participantIdentity: string
  ) => {
    try {
      const supabase = getNexusClient()

      // First get the meeting ID (cast to any for schema flexibility)
      const { data: meeting } = await (supabase
        .from('active_meetings') as any)
        .select('id')
        .eq('room_id', roomId)
        .single()

      if (!meeting) {
        console.log('Meeting not found for room:', roomId)
        return
      }

      // Upsert participant (cast to any for schema flexibility)
      await (supabase
        .from('meeting_participants') as any)
        .upsert({
          meeting_id: meeting.id,
          participant_identity: participantIdentity,
          display_name: displayName,
          admin_user_id: adminUser?.id,
          is_active: true,
          joined_at: new Date().toISOString(),
          left_at: null,
        }, {
          onConflict: 'meeting_id,participant_identity',
        })

      // Update participant count
      const { data: activeParticipants } = await (supabase
        .from('meeting_participants') as any)
        .select('id')
        .eq('meeting_id', meeting.id)
        .eq('is_active', true)

      await (supabase
        .from('active_meetings') as any)
        .update({
          participant_count: activeParticipants?.length || 0,
          last_activity_at: new Date().toISOString(),
        })
        .eq('id', meeting.id)

      // Trigger refetch to update UI
      await fetchMeetings()
    } catch (err) {
      console.error('Failed to join meeting:', err)
    }
  }, [adminUser, fetchMeetings])

  // Leave a meeting (update participant)
  const leaveMeeting = useCallback(async (
    roomId: string,
    participantIdentity: string
  ) => {
    try {
      const supabase = getNexusClient()

      // Get the meeting ID (cast to any for schema flexibility)
      const { data: meeting } = await (supabase
        .from('active_meetings') as any)
        .select('id')
        .eq('room_id', roomId)
        .single()

      if (!meeting) return

      // Update participant as inactive (cast to any for schema flexibility)
      await (supabase
        .from('meeting_participants') as any)
        .update({
          is_active: false,
          left_at: new Date().toISOString(),
        })
        .eq('meeting_id', meeting.id)
        .eq('participant_identity', participantIdentity)

      // Check if meeting has no more active participants
      const { data: remainingParticipants } = await (supabase
        .from('meeting_participants') as any)
        .select('id')
        .eq('meeting_id', meeting.id)
        .eq('is_active', true)

      const activeCount = remainingParticipants?.length || 0

      // If no active participants, delete the meeting and clean up participants
      if (activeCount === 0) {
        // Delete all participants for this meeting
        await (supabase
          .from('meeting_participants') as any)
          .delete()
          .eq('meeting_id', meeting.id)

        // Delete the meeting itself
        await (supabase
          .from('active_meetings') as any)
          .delete()
          .eq('id', meeting.id)
      } else {
        // Update participant count
        await (supabase
          .from('active_meetings') as any)
          .update({
            participant_count: activeCount,
            last_activity_at: new Date().toISOString(),
          })
          .eq('id', meeting.id)
      }

      // Trigger refetch
      await fetchMeetings()
    } catch (err) {
      console.error('Failed to leave meeting:', err)
    }
  }, [fetchMeetings])

  // Delete a meeting (host only)
  const deleteMeeting = useCallback(async (roomId: string) => {
    try {
      const supabase = getNexusClient()

      // First get the meeting ID to delete participants
      const { data: meeting } = await (supabase
        .from('active_meetings') as any)
        .select('id')
        .eq('room_id', roomId)
        .single()

      if (meeting) {
        // Delete all participants first
        await (supabase
          .from('meeting_participants') as any)
          .delete()
          .eq('meeting_id', meeting.id)
      }

      // Then delete the meeting
      await (supabase
        .from('active_meetings') as any)
        .delete()
        .eq('room_id', roomId)

      await fetchMeetings()
    } catch (err) {
      console.error('Failed to delete meeting:', err)
    }
  }, [fetchMeetings])

  // Force delete all stale meetings (ignores is_active status)
  // For persistent rooms, clears all participants but keeps the meeting
  const forceDeleteAllMeetings = useCallback(async () => {
    try {
      const supabase = getNexusClient()
      console.log('🗑️ Force deleting all meetings...')

      // Get all meetings
      const { data: allMeetings } = await (supabase
        .from('active_meetings') as any)
        .select('id, room_id')

      if (!allMeetings || allMeetings.length === 0) {
        console.log('🗑️ No meetings to delete')
        return
      }

      for (const meeting of allMeetings) {
        // For persistent rooms, just delete participants but keep the meeting
        if (meeting.room_id === 'team-conference-room') {
          console.log('🗑️ Clearing participants from persistent room:', meeting.room_id)

          // Delete all participants
          await (supabase
            .from('meeting_participants') as any)
            .delete()
            .eq('meeting_id', meeting.id)

          // Reset participant count to 0
          await (supabase
            .from('active_meetings') as any)
            .update({
              participant_count: 0,
              last_activity_at: new Date().toISOString(),
            })
            .eq('id', meeting.id)

          continue
        }

        console.log('🗑️ Deleting meeting:', meeting.room_id)

        // Delete all participants
        await (supabase
          .from('meeting_participants') as any)
          .delete()
          .eq('meeting_id', meeting.id)

        // Delete the meeting
        await (supabase
          .from('active_meetings') as any)
          .delete()
          .eq('id', meeting.id)
      }

      console.log('🗑️ Force delete complete')
      await fetchMeetings()
    } catch (err) {
      console.error('🗑️ Failed to force delete meetings:', err)
    }
  }, [fetchMeetings])

  // Persistent room IDs that should never be cleaned up
  const PERSISTENT_ROOM_IDS = ['team-conference-room']

  // Cleanup stale meetings - removes meetings with no active participants
  // Also cleans up meetings older than 24 hours regardless of participant status
  // For persistent rooms, cleans up stale participants but keeps the meeting
  const cleanupStaleMeetings = useCallback(async () => {
    try {
      const supabase = getNexusClient()
      console.log('🧹 Running meeting cleanup...')

      // Get all meetings
      const { data: allMeetings, error: fetchError } = await (supabase
        .from('active_meetings') as any)
        .select('id, room_id, participant_count, created_at, last_activity_at')

      if (fetchError) {
        console.error('🧹 Failed to fetch meetings for cleanup:', fetchError)
        return
      }

      if (!allMeetings || allMeetings.length === 0) {
        console.log('🧹 No meetings to cleanup')
        return
      }

      console.log(`🧹 Checking ${allMeetings.length} meetings...`)

      const now = new Date()
      const MAX_MEETING_AGE_HOURS = 24
      const MAX_PARTICIPANT_STALE_MINUTES = 30 // Mark participants as stale if they've been "active" for > 30 min without activity

      // Check each meeting for active participants
      for (const meeting of allMeetings) {
        const isPersistentRoom = PERSISTENT_ROOM_IDS.includes(meeting.room_id)

        // Get all participants (including their joined_at time for staleness check)
        const { data: allParticipants } = await (supabase
          .from('meeting_participants') as any)
          .select('id, display_name, is_active, joined_at')
          .eq('meeting_id', meeting.id)

        // For persistent rooms, also clean up stale participants (active for too long without recent join)
        if (isPersistentRoom && allParticipants) {
          const staleParticipantIds: string[] = []

          for (const participant of allParticipants) {
            if (participant.is_active) {
              const joinedAt = new Date(participant.joined_at)
              const minutesSinceJoin = (now.getTime() - joinedAt.getTime()) / (1000 * 60)

              // If they've been "active" for more than 30 minutes but the meeting has no recent activity,
              // they're probably stale (tab closed without proper cleanup)
              const meetingLastActivity = new Date(meeting.last_activity_at || meeting.created_at)
              const minutesSinceActivity = (now.getTime() - meetingLastActivity.getTime()) / (1000 * 60)

              if (minutesSinceJoin > MAX_PARTICIPANT_STALE_MINUTES && minutesSinceActivity > MAX_PARTICIPANT_STALE_MINUTES) {
                console.log(`🧹 Marking stale participant: ${participant.display_name} (joined ${minutesSinceJoin.toFixed(0)}min ago)`)
                staleParticipantIds.push(participant.id)
              }
            }
          }

          // Mark stale participants as inactive
          if (staleParticipantIds.length > 0) {
            await (supabase
              .from('meeting_participants') as any)
              .update({
                is_active: false,
                left_at: new Date().toISOString(),
              })
              .in('id', staleParticipantIds)

            console.log(`🧹 Marked ${staleParticipantIds.length} stale participants as inactive in ${meeting.room_id}`)
          }

          // Delete all inactive participants from persistent rooms
          const { error: deleteInactiveError } = await (supabase
            .from('meeting_participants') as any)
            .delete()
            .eq('meeting_id', meeting.id)
            .eq('is_active', false)

          if (deleteInactiveError) {
            console.error(`🧹 Failed to delete inactive participants:`, deleteInactiveError)
          }

          // Update participant count for persistent room
          const { data: remainingActive } = await (supabase
            .from('meeting_participants') as any)
            .select('id')
            .eq('meeting_id', meeting.id)
            .eq('is_active', true)

          const newCount = remainingActive?.length || 0
          if (meeting.participant_count !== newCount) {
            await (supabase
              .from('active_meetings') as any)
              .update({
                participant_count: newCount,
                last_activity_at: new Date().toISOString(),
              })
              .eq('id', meeting.id)
            console.log(`🧹 Updated ${meeting.room_id} participant count: ${meeting.participant_count} -> ${newCount}`)
          }

          continue // Don't delete persistent rooms
        }

        // For non-persistent rooms, check active participants
        const activeParticipants = allParticipants?.filter((p: any) => p.is_active) || []
        const actualActiveCount = activeParticipants.length
        const meetingAge = (now.getTime() - new Date(meeting.last_activity_at || meeting.created_at).getTime()) / (1000 * 60 * 60)

        console.log(`🧹 Meeting ${meeting.room_id}: ${actualActiveCount} active participants, ${meetingAge.toFixed(1)}h old, stored count: ${meeting.participant_count}`)

        // Delete if no active participants OR if meeting is older than 24 hours
        if (actualActiveCount === 0 || meetingAge > MAX_MEETING_AGE_HOURS) {
          console.log(`🧹 Deleting stale meeting: ${meeting.room_id} (${actualActiveCount === 0 ? 'no active participants' : 'too old'})`)

          // Delete participants first
          await (supabase
            .from('meeting_participants') as any)
            .delete()
            .eq('meeting_id', meeting.id)

          // Delete the meeting
          const { error: deleteError } = await (supabase
            .from('active_meetings') as any)
            .delete()
            .eq('id', meeting.id)

          if (deleteError) {
            console.error(`🧹 Failed to delete meeting ${meeting.room_id}:`, deleteError)
          } else {
            console.log(`🧹 Successfully deleted meeting: ${meeting.room_id}`)
          }
        } else if (meeting.participant_count !== actualActiveCount) {
          // Fix incorrect participant count
          console.log(`🧹 Fixing participant count for ${meeting.room_id}: ${meeting.participant_count} -> ${actualActiveCount}`)
          await (supabase
            .from('active_meetings') as any)
            .update({ participant_count: actualActiveCount })
            .eq('id', meeting.id)
        }
      }

      console.log('🧹 Cleanup complete')

      // Refetch to update UI
      await fetchMeetings()
    } catch (err) {
      console.error('🧹 Failed to cleanup stale meetings:', err)
    }
  }, [fetchMeetings])

  // Initial fetch, cleanup, and real-time subscription
  useEffect(() => {
    // First cleanup any stale meetings, then fetch
    cleanupStaleMeetings()

    // Set up real-time subscription
    const supabase = getNexusClient()

    const subscription = supabase
      .channel('active_meetings_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'active_meetings',
        },
        () => {
          fetchMeetings()
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'meeting_participants',
        },
        () => {
          fetchMeetings()
        }
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [fetchMeetings, cleanupStaleMeetings])

  return {
    meetings,
    isLoading,
    error,
    refetch: fetchMeetings,
    createMeeting,
    joinMeeting,
    leaveMeeting,
    deleteMeeting,
    cleanupStaleMeetings,
    forceDeleteAllMeetings,
  }
}
